import retryCallIfNeeded from '../../../restCalls';
import logger from '../../../utils/logger';
// import { personId } from '../../../../mocks/generators/wallet/v1/creditbalance_direct_deposit';
import defaultFeatureFlags from '../../../store/defaultFeatureFlags';
import flagNames from '../../../store/featureFlags';

const baseEndpointUrl = '/api/contacts/v1';
const baseEndpointUrlV2 = '/api/contacts/v2';

const getAddressValidationUrl = (street, city, state, zipCode) => {
  const [trimmedStreet, trimmedCity, trimmedState, trimmedZipCode] = [street, city, state, zipCode]
    .map(value => (value ?? '').trim());
  return `${baseEndpointUrl}/validate/address?street=${encodeURI(trimmedStreet)}&city=${encodeURI(trimmedCity)}&state=${encodeURI(trimmedState)}${trimmedZipCode ? `&zipcode=${encodeURI(trimmedZipCode)}` : ''}`;
};

/*
  URLs should be the resource path the proxy via node
  will forward to MS otherwise it will go to json-server
  refer to the task service
*/
export default function ContactService(client) {
  const getAddressSuggestionAsync = async (result) => { 
    try {
      const selected = `${result.street_line} ${result.secondary} (${result.entries}) ${result.city} ${result.state} ${result.zipcode}`;
      let prefer_secondary = '';
      if (result.country === 'US') {
        prefer_secondary = '&prefer_secondary=true';
      }

      const burl = `${baseEndpointUrlV2}/suggest/address`;
      const query = `?prefix=${encodeURI(result.street_line)}&prefer=${encodeURIComponent(result.secondary)}&prefer_ratio=1&selected=${encodeURIComponent(selected)}${prefer_secondary}`;

      const firstResponse = await client.get(`${burl}${query}`);
      const suggestions = firstResponse?.data.suggestions || [];

      const res = suggestions.map(s => ({
        // label: s.street_line,
        displayLabel: s.secondary ? `${s.street_line} ${s.secondary} ${s.zipcode}` : s.text,
        value: s,
        street_line: s.street_line,
        secondary: s.secondary,
        isExpandTrigger: false,
      }));
      return res;
    } catch (err) {
      logger.error('Erorr getting secondary suggestions ', err);
    }
  };
  const getAddressByPersonId = async personId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpointUrl}/addresses?linkId=${personId}&linkType=person`));
  const getAddressByAddressId = async addressId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpointUrl}/address/${addressId}`));
  const getEmailByPersonId = async personId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpointUrl}/emails?linkId=${personId}&linkType=person`));
  const getZipCodeValidationAsync = zipCode => retryCallIfNeeded(client, () => client
    .get(`${baseEndpointUrl}/validate/zipcode?zipcode=${encodeURI(zipCode)}`));
  const getAddressValidationAsync = (street, city, state, zipCode, secondary) => retryCallIfNeeded(
    client,
    () => client.get(`${getAddressValidationUrl(street, city, state, zipCode)}&secondary=${encodeURIComponent((secondary ?? '').trim())}&match=enhanced`),
  );
  const getStreetAddressValidationAsync = (street, city, state, zipCode) => retryCallIfNeeded(
    client,
    () => client.get(getAddressValidationUrl(street, city, state, zipCode)),
  );
  const getContactAddressByLinkIdAsync = async linkId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpointUrl}/addresses?linkId=${linkId}&pref=yes`));
  const getPhoneByPersonId = async personId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpointUrl}/phones?linkId=${personId}&linkType=person`));
  const getPhoneConsent = async linkId => retryCallIfNeeded(client, () => client
    .get(`/api/contacts/v2/consent/phone/${linkId}`));
  const getPhoneConsentAsync = async personId => retryCallIfNeeded(client, () => client
    .get(`/api/contacts/v2/consent/phone/${personId}`));
  const getEmailConsent = async personId => retryCallIfNeeded(client, () => client
    .get(`/api/contacts/consent/v1/email/${personId}`));
  const putEmail = async (emailId, payload) => retryCallIfNeeded(client, () => client
    .put(`${baseEndpointUrl}/email/${emailId}`, payload));
  const postEmail = async payload => retryCallIfNeeded(client, () => client
    .post(`${baseEndpointUrl}/email`, payload));
  const putAddress = async (addressId, payload) => retryCallIfNeeded(client, () => client
    .put(`${baseEndpointUrl}/address/${addressId}`, payload));
  const postAddress = async payload => retryCallIfNeeded(client, () => client
    .post(`${baseEndpointUrl}/address`, payload));
  const putPhone = async (phoneId, payload) => client
    .put(`${baseEndpointUrl}/phone/${phoneId}`, payload);
  const postPhone = async payload => client
    .post(`${baseEndpointUrl}/phone`, payload);
  // const putPhoneConsent = async (phoneId, payload) => retryCallIfNeeded(client, () => client
  //   .put(`/api/contacts/v2/consent/phone/${phoneId}`, payload));
  const putPhoneConsent = async payload => retryCallIfNeeded(client, () => client
    .put('/api/contacts/v2/consent/phone', payload));
  const postPhoneConsent = async payload => retryCallIfNeeded(client, () => client
    .post('/api/contacts/v2/consent/phone', payload));
  const postEmailConsent = async payload => retryCallIfNeeded(client, () => client
    .post('/api/contacts/consent/v1/email', payload));
  const getIpInformation = ipAddress => retryCallIfNeeded(client, () => client
    .get(`${baseEndpointUrl}/locationinfo/${ipAddress}`));
  const getFormattedAddressSuggestionAsync = (input, dataSetter, { cityAndState, country= '' }) => {
    if (input && input.length > 1) {
      const encodedInput = encodeURI(input);
      const encodedCityAndState = encodeURI(cityAndState);

      let url;
      let prefer_secondary = '';
      if (country === 'US') {
        prefer_secondary = '&prefer_secondary=true';
      }
      if (defaultFeatureFlags[flagNames.CONTACT_V2_ENABLED]) {
        url = `${baseEndpointUrlV2}/suggest/address?prefix=${encodedInput}&prefer=${encodedCityAndState}&prefer_ratio=1${prefer_secondary}&souce=all`;
      } else {
        url = `${baseEndpointUrl}/suggest/address?prefix=${encodedInput}&prefer=${encodedCityAndState}&prefer_ratio=1`;
      }
      client.get(url)
        .then(responseData => {
          if (Array.isArray(responseData?.data?.suggestions)) {
            const mapped = responseData.data.suggestions.flatMap(suggestion => {
              const baseOption = {
                label: country === 'US' ? suggestion.street_line : suggestion.text,
                street_line: suggestion.street_line,
                displayLabel: country === 'US' ?  (suggestion.entries > 1
                  // ? `${suggestion.text} ${suggestion.zipcode} (+${suggestion.entries} addresses)`
                  ? `${suggestion.street_line} ${suggestion.secondary} ${suggestion.city} ${suggestion.state} ${suggestion.zipcode} (+${suggestion.entries} addresses)`
                  : (`${suggestion.street_line} ${suggestion.secondary} ${suggestion.city} ${suggestion.state} ${suggestion.zipcode}`)) : suggestion.text,
                value: {
                  ...suggestion,
                  isExpandTrigger: suggestion.entries > 1,
                  units: suggestion.entries > 1 ? suggestion.unitList || [] : [],
                },
                entries: suggestion.entries,
                secondary: suggestion.secondary,
              };
              return [baseOption];
            });
            dataSetter(mapped);
          } else {
            dataSetter([]);
          }
        // eslint-disable-next-line no-console
        }).catch(error => {
          dataSetter([]);
          logger.warn(`Attempted to fetch address suggestion but received ${error?.response?.status}`);
        });
    }
  };
  return {
    getAddressByPersonId,
    getAddressByAddressId,
    getEmailByPersonId,
    postEmail,
    putEmail,
    postAddress,
    putAddress,
    getPhoneByPersonId,
    postPhone,
    putPhone,
    getPhoneConsent,
    postPhoneConsent,
    putPhoneConsent,
    getZipCodeValidationAsync,
    getAddressValidationAsync,
    getStreetAddressValidationAsync,
    getAddressSuggestionAsync,
    getFormattedAddressSuggestionAsync,
    getContactAddressByLinkIdAsync,
    getEmailConsent,
    postEmailConsent,
    getPhoneConsentAsync,
    getIpInformation,
  };
}
